/**
 * Analyst Workbench Dashboard
 *
 * Landing page for CRA Analysts.
 * Shows assigned institutions and quick access to analysis tools.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useRoleAssumption } from '../../context/RoleAssumptionContext';
import { useReportGeneration } from '../../context/ReportGenerationContext';
import { getAnalystAssignments, getSharedAssignments, type AnalystAssignment } from '../../services/firestore';
import { getRatedInstitutionsWithRatings } from '../../services/institutionService';
import type { RatedInstitutionWithRating } from '../../types/institution';
import './DashboardStyles.css';

interface InstitutionSummary {
  id: string;
  name: string;
  rating: string;
  score: number;
  date: string;
  isShared?: boolean;
}

export default function AnalystWorkbench() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { effectiveOrganization, effectiveIsPlatformAdmin, effectiveIsCRAAdmin } = useRoleAssumption();
  const { activeJobs } = useReportGeneration();
  const [institutions, setInstitutions] = useState<InstitutionSummary[]>([]);
  const [sharedInstitutions, setSharedInstitutions] = useState<InstitutionSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const firstName = userProfile?.display_name?.split(' ')[0] || 'Analyst';
  const orgName = effectiveOrganization?.name || 'Your CRA';

  // Admins see all institutions; analysts see only their assignments
  const isAdmin = effectiveIsPlatformAdmin || effectiveIsCRAAdmin;

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);

        // Fetch all rated institutions from BigQuery API
        const ratedInstitutions = await getRatedInstitutionsWithRatings();
        const ratingDataMap = new Map<string, RatedInstitutionWithRating>();
        ratedInstitutions.forEach((inst) => ratingDataMap.set(inst.institution_id, inst));

        if (isAdmin) {
          // Admins see all institutions - CRAs see all, Banks see only their access list
          const accessibleIds = effectiveOrganization?.institutions_access || [];
          const isCRAOrg = effectiveOrganization?.type === 'cra' || effectiveOrganization?.type === 'platform';

          const allInstitutions = ratedInstitutions
            .filter((inst) => {
              // CRA orgs can see all institutions (they rate all of them)
              if (isCRAOrg) return true;
              // Bank orgs only see institutions in their access list
              return accessibleIds.includes(inst.institution_id);
            })
            .map((inst) => ({
              id: inst.institution_id,
              name: inst.institution_name,
              rating: inst.rating_category || 'N/A',
              score: inst.numeric_score || 0,
              date: inst.period || '',
            }));
          setInstitutions(allInstitutions);
          setSharedInstitutions([]);
        } else if (userProfile?.uid) {
          // Analysts see only their assigned institutions
          const [myAssignments, sharedWithMe] = await Promise.all([
            getAnalystAssignments(userProfile.uid),
            getSharedAssignments(userProfile.uid),
          ]);

          // Map assignments to institution data
          const assignedInsts = myAssignments
            .filter(a => ratingDataMap.has(a.institution_id))
            .map(a => {
              const inst = ratingDataMap.get(a.institution_id)!;
              return {
                id: a.institution_id,
                name: inst.institution_name,
                rating: inst.rating_category || 'N/A',
                score: inst.numeric_score || 0,
                date: inst.period || '',
              };
            });

          const sharedInsts = sharedWithMe
            .filter(a => ratingDataMap.has(a.institution_id))
            .map(a => {
              const inst = ratingDataMap.get(a.institution_id)!;
              return {
                id: a.institution_id,
                name: inst.institution_name,
                rating: inst.rating_category || 'N/A',
                score: inst.numeric_score || 0,
                date: inst.period || '',
                isShared: true,
              };
            });

          setInstitutions(assignedInsts);
          setSharedInstitutions(sharedInsts);
        } else {
          setInstitutions([]);
          setSharedInstitutions([]);
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [effectiveOrganization, userProfile?.uid, isAdmin]);

  const getRatingClass = (rating: string): string => {
    const r = rating.toLowerCase().replace('+', '').replace('-', '');
    return `rating-${r}`;
  };

  const formatDate = (dateStr: string): string => {
    const [year, month] = dateStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  // Count active report generations
  const activeReportJobs = activeJobs.filter(
    (job) => job.status === 'generating' || job.status === 'pending'
  ).length;

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Welcome back, {firstName}</h1>
        <p className="dashboard-subtitle">{orgName} - Analyst</p>
      </div>

      <div className="dashboard-content">
        {/* Quick Actions */}
        <div className="dashboard-section">
          <h2 className="section-title">Quick Actions</h2>
          <div className="actions-grid">
            <button className="action-card primary" onClick={() => navigate('/analyst-bench')}>
              <span className="action-label">Analyst Bench</span>
            </button>
            <button className="action-card" onClick={() => navigate('/reports')}>
              <span className="action-label">My Reports</span>
            </button>
            <button className="action-card" onClick={() => navigate('/ratings')}>
              <span className="action-label">View Ratings</span>
            </button>
            <button className="action-card" onClick={() => navigate('/agents')}>
              <span className="action-label">AI Agents</span>
            </button>
          </div>
        </div>

        {/* Analyst Stats */}
        <div className="dashboard-section">
          <h2 className="section-title">Your Work</h2>
          <div className="stats-grid">
            <div
              className="stat-card"
              onClick={() => navigate('/analyst-bench')}
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-value">{loading ? '...' : institutions.length}</div>
              <div className="stat-label">Assigned Institutions</div>
            </div>
            <div
              className={`stat-card ${activeReportJobs > 0 ? 'highlight' : ''}`}
              onClick={() => navigate('/reports')}
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-value">{activeReportJobs}</div>
              <div className="stat-label">Reports In Progress</div>
            </div>
            <div
              className="stat-card"
              onClick={() => navigate('/reports')}
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-value">View</div>
              <div className="stat-label">All Reports</div>
            </div>
            <div
              className="stat-card highlight"
              onClick={() => navigate('/analyst-bench')}
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-value">Go</div>
              <div className="stat-label">Start Analysis</div>
            </div>
          </div>
        </div>

        {/* Assigned Institutions */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">
              {isAdmin ? 'All Institutions' : 'Your Institutions'}
            </h2>
            <button
              className="section-action"
              onClick={() => navigate('/analyst-bench')}
            >
              View All
            </button>
          </div>
          {institutions.length === 0 && !loading ? (
            <div className="empty-state">
              <span className="empty-icon">📋</span>
              <h3>No Institutions Assigned</h3>
              <p>Contact your CRA Admin to be assigned to institutions.</p>
            </div>
          ) : (
            <div className="institution-list">
              {institutions.slice(0, 6).map((inst) => (
                <div
                  key={inst.id}
                  className="institution-item"
                  onClick={() => navigate(`/analyst-bench/${inst.id}`)}
                >
                  <div className={`institution-rating ${getRatingClass(inst.rating)}`}>
                    {inst.rating}
                  </div>
                  <div className="institution-info">
                    <div className="institution-name">{inst.name}</div>
                    <div className="institution-score">
                      Score: {inst.score.toFixed(1)} | Updated: {formatDate(inst.date)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Shared With Me - only shown for analysts with shared access */}
        {sharedInstitutions.length > 0 && (
          <div className="dashboard-section">
            <div className="section-header">
              <h2 className="section-title">Shared With Me</h2>
            </div>
            <div className="institution-list">
              {sharedInstitutions.map((inst) => (
                <div
                  key={inst.id}
                  className="institution-item"
                  onClick={() => navigate(`/analyst-bench/${inst.id}`)}
                >
                  <div className={`institution-rating ${getRatingClass(inst.rating)}`}>
                    {inst.rating}
                  </div>
                  <div className="institution-info">
                    <div className="institution-name">
                      {inst.name} <span style={{ fontSize: '0.8em', opacity: 0.7 }}>(Shared)</span>
                    </div>
                    <div className="institution-score">
                      Score: {inst.score.toFixed(1)} | Updated: {formatDate(inst.date)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Report Jobs */}
        {activeJobs.length > 0 && (
          <div className="dashboard-section">
            <div className="section-header">
              <h2 className="section-title">Report Generation</h2>
              <button
                className="section-action"
                onClick={() => navigate('/reports')}
              >
                View All
              </button>
            </div>
            <div className="pending-list">
              {activeJobs.slice(0, 3).map((job) => (
                <div key={job.jobId} className="pending-item">
                  <div className="pending-avatar">
                    {job.status === 'generating' ? 'G' :
                     job.status === 'completed' ? 'C' :
                     job.status === 'error' ? 'E' : 'P'}
                  </div>
                  <div className="pending-info">
                    <div className="pending-name">{job.institutionName}</div>
                    <div className="pending-email">Period: {job.period}</div>
                  </div>
                  <div className="pending-time" style={{ textTransform: 'capitalize' }}>
                    {job.status}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
